import {
    ApplicationRef,
    Component,
    ComponentFactoryResolver,
    ComponentRef,
    ElementRef,
    EmbeddedViewRef,
    Injector,
    OnDestroy,
    OnInit,
    Renderer2,
    ViewChild,
    ViewEncapsulation,
    ViewRef
} from '@angular/core';
import { Observable } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { Color } from '../../../constants/color.constants';
import { Icons } from '../../../constants/icons.constants';
import { KeyCodes } from '../../../constants/keycodes.constants';
import { Modal } from '../../../models/common';
import { BackdropService } from '../../../services/backdrop/backdrop.service';
import { ModalService } from '../../../services/modal/modal.service';
import { forLifeOf, LifecycleAware } from '../../../utilities/rxjs.helpers';
import { Utilities } from '../../../utilities/utilities.helper';

@LifecycleAware()
@Component({
    selector: 'lsq-modal',
    template: `
        <div
            *ngIf="modal"
            #modalElement
            class="modal {{ modal.className ? modal.className : '' }}"
            tabindex="-1"
            role="dialog"
            (keydown)="handleModalKeyDown($event)"
            (mousedown)="handleBackdropClick()"
            [id]="modal.id || ''"
            [ngClass]="{
                showing: showModal,
                hidden: !showModal,
                'block-scroll': blockScrollStream | async
            }"
        >
            <!-- blockClickThrough prevents the mousedown event propagating to the backdrop -->
            <div
                class="modal-dialog"
                #modalDialogElement
                (mousedown)="blockClickThrough($event)"
                role="document"
                [ngStyle]="{
                    width: modal.width
                }"
            >
                <!-- A component will be inserted before this element if modal.component is truthy -->
                <span
                    *ngIf="modal.component"
                    #componentHostElement
                    [ngStyle]="{ visibility: 'hidden' }"
                >
                </span>
                <div
                    *ngIf="!modal.component"
                    class="modal-content"
                    [class]="{
                        'no-bottom-padding': !modal.showOkButton && !modal.showCancelButton
                    }"
                    [ngStyle]="getModalContentStyle()"
                >
                    <!-- Header -->
                    <div class="modal-header" *ngIf="modal.showHeader || modal.showCloseButton">
                        <h1 *ngIf="modal.showHeader">
                            {{ modal.title }}
                        </h1>
                        <lsq-button
                            *ngIf="modal.showCloseButton"
                            appearance="transparent"
                            class="close"
                            ariaLabel="Close"
                            (buttonClick)="handleCloseButtonClick()"
                        >
                            <lsq-icon
                                aria-hidden="true"
                                [type]="icons.Close"
                                [color]="colors.Black"
                            >
                            </lsq-icon>
                        </lsq-button>
                    </div>

                    <div #modalBodyElement class="modal-body">
                        <!-- A component will be inserted before this element if modal.bodyComponent is truthy -->
                        <span
                            *ngIf="modal.bodyComponent"
                            #bodyComponentHostElement
                            [ngStyle]="{ visibility: 'hidden' }"
                        >
                        </span>

                        <!-- A template will be inserted here if modal.templateRef is truthy -->
                        <ng-container *ngIf="modal.templateRef">
                            <ng-container
                                *ngTemplateOutlet="modal.templateRef; context: modal.context"
                            >
                            </ng-container>
                        </ng-container>

                        <p *ngIf="modal.bodyText">{{ modal.bodyText }}</p>
                        <div *ngIf="modal.bodyHtml" [innerHTML]="modal.bodyHtml"></div>
                    </div>

                    <!-- Footer -->
                    <div
                        *ngIf="modal.showFooter"
                        class="modal-footer"
                        [ngClass]="{ 'ok-btn-showing': modal.showOkButton }"
                    >
                        <div class="modal-footer-left" *ngIf="modal.showCancelButton">
                            <lsq-button
                                *ngIf="modal.showCancelButton"
                                appearance="secondary"
                                buttonClass="cancel-btn pull-left"
                                (buttonClick)="handleCancelButtonClick()"
                            >
                                {{ modal.cancelButtonText }}
                            </lsq-button>
                        </div>

                        <lsq-button
                            class="modal-footer-right"
                            [class.noCancelButton]="!modal.showCancelButton"
                            *ngIf="modal.showOkButton"
                            appearance="primary"
                            buttonClass="ok-btn"
                            [size]="modal.okButtonSize"
                            [submitting]="
                                modal.isSubmitting$ ? (modal.isSubmitting$ | async) : false
                            "
                            [disabled]="
                                modal.okButtonIsDisabled$
                                    ? (modal.okButtonIsDisabled$ | async)
                                    : false
                            "
                            (buttonClick)="handleOkButtonClick()"
                        >
                            {{ modal.okButtonText }}
                        </lsq-button>
                    </div>
                </div>
            </div>
        </div>
    `,
    styleUrls: ['./lsq-modal.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class LsqModalComponent implements OnInit, OnDestroy {
    @ViewChild('modalElement') public modalElement: ElementRef;
    @ViewChild('modalBodyElement') public modalBodyElement: ElementRef;
    @ViewChild('modalDialogElement') public modalDialogElement: ElementRef;
    @ViewChild('bodyComponentHostElement') public bodyComponentHostElement: ElementRef;
    @ViewChild('componentHostElement') public componentHostElement: ElementRef;
    public showModal = false;
    public previousMinHeight = 0;
    public previousMinHeightShouldApply = false;
    public modal: Modal;
    public icons = Icons;
    public colors = Color;
    public viewRefs: ViewRef[] = [];
    public blockScrollStream: Observable<boolean>;

    constructor(
        private modalService: ModalService,
        private componentFactoryResolver: ComponentFactoryResolver,
        private applicationRef: ApplicationRef,
        private injector: Injector,
        private renderer: Renderer2,
        public backdropService: BackdropService
    ) {
        this.blockScrollStream = this.modalService.blockScrollStream;
    }

    public ngOnInit(): void {
        this.modalService.modalStream
            .pipe(forLifeOf(this), distinctUntilChanged())
            .subscribe(async (newModal) => {
                if (newModal) {
                    await this.loadModal(newModal);
                } else {
                    this.modal = null;
                }
            });
    }

    public ngOnDestroy(): void {}

    public async loadModal(newModal: Modal): Promise<void> {
        const oldModal = this.modal;
        this.modal = newModal;
        this.backdropService.openBackdrop();
        this.previousMinHeightShouldApply = true;

        if (this.modal.component) {
            await this._loadEntireComponent();
        } else if (this.modal.bodyComponent) {
            await this._loadBodyComponent();
        }

        this._cleanUpComponentRefs(oldModal, this.modal);

        this._displayModal();
    }

    // TODO(story/11741): Clean up expensive template methods like this.
    public getModalContentStyle(): object {
        return {
            'min-height':
                this.previousMinHeight * (this.previousMinHeightShouldApply ? 1 : 0) + 'px'
        };
    }

    public closeModal(): void {
        this.modalService.closeModal();
        this.showModal = false;
    }

    public handleCancelButtonClick(): void {
        if (!!this.modal.onCancelClick) {
            if (this.modal.context) {
                this.modal.onCancelClick.call(this.modal.context);
            } else {
                this.modal.onCancelClick();
            }
        } else {
            this.closeModal();
        }
    }

    public handleCloseButtonClick(): void {
        if (!!this.modal.onCloseButtonClick) {
            if (this.modal.context) {
                this.modal.onCloseButtonClick.call(this.modal.context);
            } else {
                this.modal.onCloseButtonClick();
            }
        } else {
            this.closeModal();
        }
    }

    public handleOkButtonClick(): void {
        if (!this.modal) {
            return;
        }

        if (!this.modal.onOkClick) {
            this.closeModal();
            return;
        }

        if (this.modal.context) {
            this.modal.onOkClick.call(this.modal.context);
        } else {
            this.modal.onOkClick();
        }
    }

    public handleModalKeyDown(event: KeyboardEvent): void {
        if (event.which === KeyCodes.Escape) {
            this.closeModal();
        }
    }

    public handleBackdropClick(): void {
        if (!!this.modal.onBackdropClick) {
            if (this.modal.context) {
                this.modal.onBackdropClick.call(this.modal.context);
            } else {
                this.modal.onBackdropClick();
            }
        } else {
            if (this.modal.clickOutsideToClose) {
                this.closeModal();
            }
        }
    }

    public blockClickThrough(event: Event): void {
        event.stopPropagation();
    }

    private _cleanUpComponentRefs(oldModal: Modal, newModal: Modal): void {
        const newModalComponentRef = newModal && newModal.componentRef;
        const newModalBodyComponentRef = newModal && newModal.bodyComponentRef;

        if (oldModal && oldModal.componentRef && newModalComponentRef !== oldModal.componentRef) {
            oldModal.componentRef.destroy();
            oldModal.componentRef = null;
        }
        if (
            oldModal &&
            oldModal.bodyComponentRef &&
            newModalBodyComponentRef !== oldModal.bodyComponentRef
        ) {
            oldModal.bodyComponentRef.destroy();
            oldModal.bodyComponentRef = null;
        }
    }

    private _loadComponentIntoTemplate(
        refToInsert: ComponentRef<any>,
        componentToInsert: any,
        hostRef: ElementRef,
        hostParentRef: ElementRef,
        isEntireModal = false
    ): void {
        let componentRef = refToInsert;

        if (!componentRef) {
            componentRef = this.componentFactoryResolver
                .resolveComponentFactory(componentToInsert)
                .create(this.injector);
            if (isEntireModal) {
                this.modal.componentRef = componentRef;
            } else {
                this.modal.bodyComponentRef = componentRef;
            }
        }

        this.applicationRef.attachView(componentRef.hostView);
        this.viewRefs.push(componentRef.hostView);

        if (this.viewRefs.length > 1) {
            this.applicationRef.detachView(this.viewRefs.shift());
        }

        const componentDomElement = (componentRef.hostView as EmbeddedViewRef<any>)
            .rootNodes[0] as HTMLElement;
        if (componentDomElement && hostParentRef && hostRef) {
            this.renderer.insertBefore(
                hostParentRef.nativeElement,
                componentDomElement,
                hostRef.nativeElement
            );
        }
    }

    private _loadBodyComponent(): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            // @reference https://medium.com/@caroso1222/angular-pro-tip-how-to-dynamically-create-components-in-body-ba200cc289e6
            // Need to wait a for our `*ngIf="modal"` to be rendered.
            Utilities.executeAfterRender(() => {
                if (!!this.modal) {
                    this._loadComponentIntoTemplate(
                        this.modal.bodyComponentRef,
                        this.modal.bodyComponent,
                        this.bodyComponentHostElement,
                        this.modalBodyElement
                    );

                    // Now we need to wait for the above addition to the DOM to be rendered.
                    Utilities.executeAfterRender(() => {
                        resolve();
                    });
                } else {
                    resolve();
                }
            });
        });
    }

    private _loadEntireComponent(): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            // @reference https://medium.com/@caroso1222/angular-pro-tip-how-to-dynamically-create-components-in-body-ba200cc289e6
            // Need to wait a for our `*ngIf="modal"` to be rendered.
            Utilities.executeAfterRender(() => {
                if (!!this.modal) {
                    this._loadComponentIntoTemplate(
                        null,
                        this.modal.component,
                        this.componentHostElement,
                        this.modalDialogElement,
                        true
                    );

                    // Now we need to wait for the above addition to the DOM to be rendered.
                    Utilities.executeAfterRender(() => {
                        resolve();
                    });
                } else {
                    resolve();
                }
            });
        });
    }

    private _displayModal(): void {
        this.showModal = true;

        Utilities.executeAfterRender(() => {
            if (this.modalElement) {
                this.modalElement.nativeElement.focus();
            }
        });

        if (this.modalBodyElement) {
            this.previousMinHeight = (this.modalBodyElement
                .nativeElement as HTMLElement).offsetHeight;

            Utilities.executeAfterRender(() => {
                this.previousMinHeightShouldApply = false;
            });
        }
    }
}
